import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { IActivity, KeyStage, InputMode } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { useWorkpadStore } from '../store/useWorkpadStore';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { useActivityWorkpad } from '../hooks/useActivityWorkpad';
import { SkiaCanvas } from './SkiaCanvas';
import { MultiModalInputArea } from './MultiModalInputArea';
import { useActivityInputModes } from '../hooks/useActivityInputModes';
import { StageCard, TwoToneCounter, TactileCircleToken, TileNumber } from './primitives';

interface MathGridActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

export const MathGridActivity: React.FC<MathGridActivityProps> = ({ activity, onComplete }) => {
    const [answer, setAnswer] = useState('');
    const [activeMode, setActiveMode] = useState<InputMode>('numpad');

    const { setStructuredData } = useStrokeStore.getState();
    const { workingOut } = useWorkpadStore();
    const { workpad: workpadEnabled } = useFeatureFlags();
    const { requestScaffoldOnIncorrect } = useActivityWorkpad();

    const expression = activity.config?.expression || '';
    const expectedAnswer = activity.config?.expectedAnswer;

    const flags = useFeatureFlags();
    const { allowedModes, defaultMode } = useActivityInputModes(activity);
    const multiModalEnabled = flags.inputModeSwitcherEnabled && allowedModes.length > 1;

    const canSubmit = answer.trim().length > 0;

    const isCorrect = (): boolean => {
        if (expectedAnswer === undefined || expectedAnswer === null) return true;
        const numAnswer = Number(answer);
        const numExpected = Number(expectedAnswer);
        return !isNaN(numAnswer) && numAnswer === numExpected;
    };

    const handleSubmit = () => {
        if (!canSubmit) return;
        const correct = isCorrect();

        // Auto-open guided workpad on wrong answer
        if (!correct && workpadEnabled) {
            const opened = requestScaffoldOnIncorrect();
            if (opened) return;
        }

        const structuredData: Record<string, unknown> = { answer, inputModeUsed: activeMode };
        if (workingOut) {
            structuredData.workingOut = workingOut;
        }

        setStructuredData(structuredData);
        onComplete();
    };

    const handleNumpadPress = (num: string) => setAnswer(prev => prev + num);
    const handleClear = () => setAnswer('');

    const parsedExpr = React.useMemo(() => {
        const match = expression.match(/^\s*(\d+)\s*([+\-*/])\s*(\d+)\s*$/);
        if (!match) return null;
        return {
            first: parseInt(match[1], 10),
            op: match[2],
            second: parseInt(match[3], 10),
        };
    }, [expression]);

    return (
        <ScrollView
            contentContainerStyle={styles.scrollContent}
            style={styles.container}
            showsVerticalScrollIndicator={false}
            bounces={false}
        >
            <StageCard style={styles.stageCard}>
                {parsedExpr && (
                    <View style={styles.manipulativesRow}>
                        <TileNumber value={parsedExpr.first} theme="green" size={46} />
                        <Text style={styles.manipulativeOperator}>{parsedExpr.op}</Text>
                        <TwoToneCounter topColor="red" bottomColor="yellow" size={32} />
                        <Text style={styles.manipulativeArrow}>➔</Text>
                        <TactileCircleToken color="blue" size={44} />
                        <Text style={styles.manipulativeOperator}>=</Text>
                        <TactileCircleToken color="red" size={46} badgeValue={parsedExpr.first} />
                    </View>
                )}

                <View style={styles.displayArea}>
                    <Text style={styles.expression} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{expression}</Text>
                    <View style={styles.answerBox}>
                        <Text testID="answer-display" style={styles.answer} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{answer || ' '}</Text>
                    </View>
                </View>

                {multiModalEnabled ? (
                    <View testID="math-input-area" style={styles.multiModalArea}>
                        <MultiModalInputArea
                            allowedModes={allowedModes}
                            defaultMode={defaultMode}
                            onChange={(val) => setAnswer(val)}
                            onSubmit={(val, mode, meta) => {
                                setAnswer(val);
                                if (meta?.voiceTranscript) {
                                    useStrokeStore.getState().setStructuredData({
                                        voiceTranscript: meta.voiceTranscript,
                                        inputModeUsed: mode,
                                    });
                                }
                                setActiveMode(mode);
                                onComplete();
                            }}
                            onModeChange={(mode) => setActiveMode(mode)}
                            placeholder="Enter your answer..."
                            keyboardType="numeric"
                        />
                    </View>
                ) : (
                    <View style={styles.numpad}>
                        <View style={styles.row}>
                            {[1, 2, 3].map(num => (
                                <TouchableOpacity key={num} style={styles.numButton} onPress={() => handleNumpadPress(num.toString())} activeOpacity={0.7}
                                    accessibilityRole="button"
                                    accessibilityLabel={`Number ${num}`}
                                >
                                    <Text style={styles.numText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{num}</Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                        <View style={styles.row}>
                            {[4, 5, 6].map(num => (
                                <TouchableOpacity key={num} style={styles.numButton} onPress={() => handleNumpadPress(num.toString())} activeOpacity={0.7}
                                    accessibilityRole="button"
                                    accessibilityLabel={`Number ${num}`}
                                >
                                    <Text style={styles.numText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{num}</Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                        <View style={styles.row}>
                            {[7, 8, 9].map(num => (
                                <TouchableOpacity key={num} style={styles.numButton} onPress={() => handleNumpadPress(num.toString())} activeOpacity={0.7}
                                    accessibilityRole="button"
                                    accessibilityLabel={`Number ${num}`}
                                >
                                    <Text style={styles.numText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{num}</Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                        <View style={styles.row}>
                            <TouchableOpacity style={styles.actionButton} onPress={handleClear} activeOpacity={0.7}
                                accessibilityRole="button"
                                accessibilityLabel="Clear answer"
                                accessibilityHint="Clears the current answer"
                            >
                                <Text style={styles.actionText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>C</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.numButton} onPress={() => handleNumpadPress('0')} activeOpacity={0.7}
                                accessibilityRole="button"
                                accessibilityLabel="Number 0"
                            >
                                <Text style={styles.numText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>0</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[styles.submitButton, !canSubmit && styles.disabledButton]}
                                onPress={handleSubmit}
                                disabled={!canSubmit}
                                activeOpacity={0.8}
                                accessibilityRole="button"
                                accessibilityLabel="Done"
                                accessibilityHint="Submits your answer"
                                accessibilityState={{ disabled: !canSubmit }}
                            >
                                <Text style={styles.submitText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>GO!</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                )}
            </StageCard>

            {/* Teacher Feedback Overlay (Read-Only Canvas) */}
            <View style={StyleSheet.absoluteFill} pointerEvents="none">
                <SkiaCanvas readOnly={true} />
            </View>
        </ScrollView>
    );

};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    scrollContent: {
        flexGrow: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 16,
        paddingVertical: 16,
    },
    stageCard: {
        marginVertical: 8,
        alignItems: 'center',
        justifyContent: 'center',
    },
    manipulativesRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        marginBottom: 16,
        flexWrap: 'wrap',
    },
    manipulativeOperator: {
        fontSize: 32,
        fontWeight: '900',
        color: '#1E293B',
    },
    manipulativeArrow: {
        fontSize: 28,
        fontWeight: '900',
        color: '#0D9488',
    },
    displayArea: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        marginVertical: 12,
        gap: 16,
        // alignSelf stretch (not width:'100%'): a percentage width inside
        // StageCard's shrink-wrapped innerContent resolves to intrinsic
        // content size under Yoga, collapsing the row on native.
        alignSelf: 'stretch',
        maxWidth: 420,
    },
    expression: {
        fontSize: 38,
        fontWeight: 'bold',
        color: '#1E293B',
        letterSpacing: 1,
    },
    answerBox: {
        height: 64,
        minWidth: 120,
        borderWidth: 2.5,
        borderColor: '#93C5FD',
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 16,
        backgroundColor: '#F8FAFC',
        shadowColor: '#3B82F6',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.12,
        shadowRadius: 6,
        elevation: 3,
    },
    answer: {
        fontSize: 36,
        fontWeight: '900',
        color: '#2563EB',
    },
    multiModalArea: {
        // No flex (zero-basis flex collapses to 0 height inside the
        // indefinite-height ScrollView content) and alignSelf stretch rather
        // than width:'100%' (percentage width inside the shrink-wrapped
        // StageCard resolves to intrinsic size under Yoga).
        alignSelf: 'stretch',
        maxWidth: 420,
        paddingHorizontal: 12,
    },
    numpad: {
        // alignSelf stretch (not width:'100%') — see displayArea above. Under
        // Yoga the '100%' resolved to the rows' intrinsic width (~33px,
        // because the flex:1 buttons contribute ~0 intrinsic width), leaving
        // ~3px-wide buttons on native while web rendered fine.
        alignSelf: 'stretch',
        maxWidth: 380,
        gap: 12,
        marginVertical: 8,
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        gap: 12,
    },
    numButton: {
        flex: 1,
        height: 60,
        minHeight: 56,
        backgroundColor: '#FFFFFF',
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        borderBottomWidth: 4,
        borderBottomColor: '#CBD5E1',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
        elevation: 2,
    },
    numText: {
        fontSize: 26,
        fontWeight: '900',
        color: '#1E293B',
    },
    actionButton: {
        flex: 1,
        height: 60,
        minHeight: 56,
        backgroundColor: '#FFF1F2',
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: '#FECDD3',
        borderBottomWidth: 4,
        borderBottomColor: '#FDA4AF',
        shadowColor: '#EF4444',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 3,
        elevation: 2,
    },
    actionText: {
        fontSize: 26,
        fontWeight: '900',
        color: '#E11D48',
    },
    submitButton: {
        flex: 2,
        height: 60,
        minHeight: 56,
        backgroundColor: '#22C55E',
        borderRadius: 9999,
        alignItems: 'center',
        justifyContent: 'center',
        borderBottomWidth: 5,
        borderBottomColor: '#15803D',
        borderTopWidth: 1,
        borderTopColor: 'rgba(255, 255, 255, 0.4)',
        shadowColor: '#15803D',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.35,
        shadowRadius: 6,
        elevation: 5,
    },
    disabledButton: {
        backgroundColor: '#94A3B8',
        borderBottomColor: '#64748B',
        shadowOpacity: 0,
        elevation: 0,
    },
    submitText: {
        fontSize: 22,
        fontWeight: '900',
        color: '#FFFFFF',
        letterSpacing: 1.2,
    },
});

